import { useSyncExternalStore } from "react";

import { resolveIsDark, type ThemePreference } from "@tsmono/theme/bootstrap";

// Re-render trigger: watch the `data-bs-theme` attribute the theme bootstrap
// writes to <html>. That's the single source of truth both the pre-paint
// inline script and React share, and it's the only signal for theme changes
// that don't flow through React state — an OS color-scheme flip or a VS Code
// host theme swap (`vscode-dark`↔`vscode-light`), both of which the bootstrap
// turns into a `data-bs-theme` update.
const subscribe = (onChange: () => void): (() => void) => {
  if (
    typeof MutationObserver === "undefined" ||
    typeof document === "undefined"
  ) {
    return () => {};
  }
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-bs-theme"],
  });
  return () => observer.disconnect();
};

/**
 * Resolved dark/light for the theme toggle icon. The value is derived from the
 * preference (so an in-app pick flips the icon in the same render); the
 * subscription re-renders on external changes the preference can't see.
 */
export const useResolvedIsDark = (preference: ThemePreference): boolean =>
  useSyncExternalStore(
    subscribe,
    () => resolveIsDark(preference),
    // No SSR today; light is the safe default if a render ever runs serverside.
    () => false
  );
